import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React, {useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {assert, assertAndContinue} from 'Asserts';
import {Owner, SchemaCollaborator} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {RESULTS_ANALYTICS_ACTIONS, SCHEMAS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SavingState} from 'constants/RegistryConstants';
import * as Context from 'Context';
import {
  ActionMethod,
  ActionOrigin,
  LegacyAssaySchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {AutomationInputFileConfigListEditor} from 'lab-automation/settings/automation-input-file-config/AutomationInputFileConfigListEditor';
import {AutomationOutputFileConfigListEditor} from 'lab-automation/settings/automation-output-file-config/AutomationOutputFileConfigListEditor';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
  FileConfigErrors,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {getStore} from 'redux-stores/Store';
import FieldTable, {
  BaseFieldTableProps,
  ClientSchemaField,
  FieldTableLoading,
} from 'registry/field/FieldTable';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getOwnerById, massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {getServerOutputFileConfigs} from 'schemas/spec/reduceSchemaConfigUI';
import {AssaySchemaSpec, RunSchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import {
  filterSchemasByOrganizationJson,
  filterSchemasByRegistryOrganization,
} from 'schemas/util/FilterSchemasByOrganization';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import * as modals from 'util/Modals';
import {withLoadingScreen} from 'util/Promises';
import {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';
import {AssaySchemaMetadataSettingsQuery} from './__generated/AssaySchemaMetadataSettingsQuery';
import {AssayRunSchema, AssaySchema, getJsonUrl, SCHEMA_TYPE_NAME} from './AssaySchemaConstants';
import AssaySchemaErrorList from './AssaySchemaErrorList';
import {ResultSchemaFieldTableRow, RunSchemaFieldTableRow} from './AssaySchemaFieldTableRow';
import RunSchemaMetadataSection from './RunSchemaMetadataSection';
import SchemaMetadataSection from './SchemaMetadataSection';

export const ASSAY_SCHEMA_METADATA_SETTINGS_QUERY: DocumentNode = gql`
  query AssaySchemaMetadataSettingsQuery @skipAudit @engComponent(name: RESULTS) {
    ...AllAssaySchemasGraphQL
    boxSchemas {
      ...BoxSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    containerSchemas {
      ...ContainerSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    locationSchemas {
      ...LocationSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    plateSchemas {
      ...PlateSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    tagSchemas {
      ...TagSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
  ${getFragmentWrapper('BoxSchema').fragment}
  ${getFragmentWrapper('ContainerSchema').fragment}
  ${getFragmentWrapper('LocationSchema').fragment}
  ${getFragmentWrapper('PlateSchema').fragment}
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('Registry').fragment}
`;

export interface AssaySchemaMetadataSettingsProps {
  // From AssaySchemaEditorProps
  schemaId?: string;
  schemaType: LegacyAssaySchemaType.ASSAY_RESULT | LegacyAssaySchemaType.ASSAY_RUN;
  autosave: boolean;
  baseTestAttrs: {component: string};
  publishDefaultDataTableConfig: boolean;
  // From AssaySchemaEditorQueriedProps
  schemaHasData: boolean; // Have any items of this schema been created?
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummariesById: BaseFieldTableProps['schemaFieldDependentsSummariesById'];
  // From AssaySchemaEditorConnectedProps
  errors: Array<string> | null;
  errorMessage: string | null;
  ownerChoices: Array<Owner> | null;
  ownerId: string;
  name: string;
  sqlIdentifier: string | null;
  schema: AssaySchema | null;
  inputFileConfigs: Array<AutomationInputFileConfigSpec>;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>;
  fields: Array<ClientSchemaField | SchemaField> | null;
  hasUnsavedData: boolean;
  hasUnsavedInputFileConfigData: boolean;
  hasUnsavedOutputFileConfigData: boolean;
  allowCreateRunFromNotebook: boolean;
  allowInsertRunFromInbox: boolean;
  shouldRecordEquipment: boolean;
  connectionSchemaId: string | null;
  schemaPermissions: SchemaPermissionsGraphQL | null;
  sessionId: string;
  // New
  shouldRenderPermissionSettingsTab: boolean;
  unsavedDataMessage: string;
  handleNext?: () => void;
}

function filterDataToOrganization(
  queryResult: AssaySchemaMetadataSettingsQuery,
  organizationId: string
): AssaySchemaMetadataSettingsQuery {
  const data = {...queryResult};
  data.tagSchemas = filterSchemasByRegistryOrganization(queryResult.tagSchemas, organizationId);
  data.boxSchemas = filterSchemasByRegistryOrganization(queryResult.boxSchemas, organizationId);
  data.containerSchemas = filterSchemasByRegistryOrganization(queryResult.containerSchemas, organizationId);
  data.locationSchemas = filterSchemasByRegistryOrganization(queryResult.locationSchemas, organizationId);
  data.plateSchemas = filterSchemasByRegistryOrganization(queryResult.plateSchemas, organizationId);

  data.resultSchemas = filterSchemasByOrganizationJson(queryResult.resultSchemas, organizationId);
  data.runSchemas = filterSchemasByOrganizationJson(queryResult.runSchemas, organizationId);
  data.requestSchemas = filterSchemasByOrganizationJson(queryResult.requestSchemas, organizationId);

  return data;
}

function massageFieldsForSubmitWithSpecErrorHandling(
  fields: Array<ClientSchemaField | SchemaField>
): Array<SchemaField> | null {
  try {
    return massageFieldsForSubmit(fields);
  } catch (e) {
    if (e instanceof FieldNamingException) {
      getStore().dispatch(
        SchemaConfigReduxActions.setSchemaSpecError({
          errors: [],
          errorMessage: e.message,
        })
      );
      return null;
    } else {
      throw e;
    }
  }
}

function getSpecForRequest(props: AssaySchemaMetadataSettingsProps): AssaySchemaSpec | RunSchemaSpec | null {
  assertAndContinue(props.fields != null, '"fields" must be defined before constructing the spec.');
  if (props.fields?.map((field) => field.name).includes('')) {
    getStore().dispatch(
      SchemaConfigReduxActions.setSchemaSpecError({
        errors: [],
        errorMessage: `"fields" must all have names.`,
      })
    );
    return null;
  }
  const fields = massageFieldsForSubmitWithSpecErrorHandling(props.fields!);
  if (fields == null) {
    return null;
  }

  if (props.schemaType === LegacyAssaySchemaType.ASSAY_RUN) {
    const spec = {
      fields,
      automationInputFileConfigs: props.inputFileConfigs,
      allowCreateRunFromNotebook: props.allowCreateRunFromNotebook,
      allowInsertRunFromInbox: props.allowInsertRunFromInbox,
      shouldRecordEquipment: props.shouldRecordEquipment,
      connectionSchemaId: props.connectionSchemaId,
    };
    return {
      ...spec,
      automationOutputFileConfigs: getServerOutputFileConfigs(props.outputFileConfigs),
    };
  } else {
    return {fields};
  }
}

function trackLabAutomationConfigUpdates(props: AssaySchemaMetadataSettingsProps): void {
  const metadata = {
    action_origin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
    action_method: ActionMethod.SETTINGS_EDIT,
    schema_type: props.schemaType,
    schema_id: props.schemaId,
    session_id: props.sessionId,
  };
  if (props.hasUnsavedInputFileConfigData) {
    analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.UPDATED_INPUT_FILE_CONFIG, metadata);
  }
  if (props.hasUnsavedOutputFileConfigData) {
    analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.UPDATED_OUTPUT_FILE_CONFIG, metadata);
  }
}

export function createAssaySchema(
  props: AssaySchemaMetadataSettingsProps,
  collaborators?: Array<SchemaCollaborator>
): void {
  const spec = getSpecForRequest(props);
  if (spec) {
    Context.using(
      {
        analyticsMetadata: {
          actionOrigin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
          actionMethod: ActionMethod.CREATE_BUTTON,
        },
      },
      null,
      () => {
        AssaySchemaActions.createAssaySchema({
          ownerId: props.ownerId,
          name: props.name,
          schemaType: props.schemaType,
          spec,
          sessionId: props.sessionId,
          sqlIdentifier:
            BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && props.sqlIdentifier
              ? props.sqlIdentifier
              : null,
          isSpecJsonFormat: false,
          publishDefaultDataTableConfig: props.publishDefaultDataTableConfig,
          collaborators,
        });
      }
    );
  }
}

export default function AssaySchemaMetadataSettings(props: AssaySchemaMetadataSettingsProps): JSX.Element {
  const [specErrors, setSpecErrors] = useState<Array<string>>([]);
  const [fieldsErrors, setFieldsErrors] = useState<Array<string>>([]);
  const [inputFileConfigErrors, setInputFileConfigErrors] = useState<FileConfigErrors>({generic: []});
  const [outputFileConfigErrors, setOutputFileConfigErrors] = useState<FileConfigErrors>({generic: []});
  const [componentsAreValid, setComponentsAreValid] = useState({
    automationInputFileConfigs: true,
    automationOutputFileConfigs: true,
  });

  const queryResult = useBenchlingQuery<AssaySchemaMetadataSettingsQuery>(
    ASSAY_SCHEMA_METADATA_SETTINGS_QUERY
  );
  const linkableSchemas = useMemo(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      const data = filterDataToOrganization(queryResult.data, props.ownerId);

      return {
        assaySchemas: [
          ...graphQLArrayToREST('RunSchema', data.runSchemas),
          ...graphQLArrayToREST('RequestSchema', data.requestSchemas),
          ...graphQLArrayToREST('ResultSchema', data.resultSchemas),
        ],
        storableSchemas: [
          ...graphQLArrayToREST('ContainerSchema', data.containerSchemas),
          ...graphQLArrayToREST('BoxSchema', data.boxSchemas),
          ...graphQLArrayToREST('LocationSchema', data.locationSchemas),
          ...graphQLArrayToREST('PlateSchema', data.plateSchemas),
        ],
      };
    }
    return {
      assaySchemas: [],
      storableSchemas: [],
    };
  }, [queryResult, props.ownerId]);

  useEffect(() => {
    const indexPattern = / at index (\d+)$/;
    const newSpecErrors = [];
    const newFieldsErrors = [];
    const newInputFileConfigErrors: FileConfigErrors = {generic: []};
    const newOutputFileConfigErrors: FileConfigErrors = {generic: []};
    const getKeyOfError = (errorMessage: string): keyof FileConfigErrors => {
      const match = errorMessage.match(indexPattern);
      if (!match) {
        return 'generic';
      }
      return parseInt(match[1], 10);
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const addToErrorObj = (errorObj: FileConfigErrors, errorMessage: string, key: keyof FileConfigErrors) => {
      if (!errorObj[key]) {
        errorObj[key] = [];
      }
      errorObj[key].push(errorMessage.replace(indexPattern, ''));
    };

    if (props.errors != null) {
      for (const error of props.errors) {
        if (error.match(/^"fields.*$/)) {
          newFieldsErrors.push(error);
        } else if (error.match(/^"automationInputFileConfigs.*$/g)) {
          const key = getKeyOfError(error);
          addToErrorObj(newInputFileConfigErrors, error, key);
        } else if (error.match(/^"automationOutputFileConfigs.*$/g)) {
          const key = getKeyOfError(error);
          addToErrorObj(newOutputFileConfigErrors, error, key);
        } else {
          newSpecErrors.push(error);
        }
      }
    }

    setSpecErrors(newSpecErrors);
    setFieldsErrors(newFieldsErrors);
    setInputFileConfigErrors(newInputFileConfigErrors);
    setOutputFileConfigErrors(newOutputFileConfigErrors);
  }, [props.errors]);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const updateAssaySchema = ({
    name,
    sqlIdentifier,
    spec,
    draft,
  }: {
    name?: string;
    sqlIdentifier?: string;
    spec?: AssaySchemaSpec | null;
    draft?: AssaySchemaSpec | null;
  } = {}) => {
    spec = spec || getSpecForRequest(props);
    if (spec == null) {
      return;
    }
    Context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
        },
      },
      null,
      () => {
        withLoadingScreen(
          AssaySchemaActions.updateAssaySchema({
            schemaId: props.schemaId!,
            schemaName: name ?? props.name,
            sqlIdentifier: BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME
              ? (sqlIdentifier ?? props.sqlIdentifier)
              : null,
            spec: spec!,
            sessionId: props.sessionId,
            isSpecJsonFormat: false,
            publishDefaultDataTableConfig: props.publishDefaultDataTableConfig,
          }).catch((response) => {
            getStore().dispatch(
              SchemaConfigReduxActions.schemaSpecResetToDraft(JSON.stringify(draft ?? spec))
            );
          }),
          'Updating...'
        );
      }
    );
    trackLabAutomationConfigUpdates(props);
  };

  let buttonText;
  let dataTestAttributeElement: string;
  let onButtonClick: () => void;
  if (props.schemaId) {
    buttonText = 'Update';
    dataTestAttributeElement = 'update';

    onButtonClick = updateAssaySchema;
  } else if (
    BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS &&
    props.schemaId == null &&
    props.handleNext
  ) {
    buttonText = 'Next';
    dataTestAttributeElement = 'next';
    onButtonClick = props.handleNext;
  } else {
    buttonText = 'Create';
    dataTestAttributeElement = 'create';
    onButtonClick = () => {
      createAssaySchema(props);
    };
  }

  function shouldAllowSchemaUpdates(): boolean {
    if (props.shouldRenderPermissionSettingsTab) {
      return props.schemaPermissions != null && props.schemaPermissions.definitionEditable === true;
    } else {
      return true;
    }
  }

  const onEditJSONClick = async (): Promise<void> => {
    if (props.hasUnsavedData) {
      const confirm = await Promise.resolve(
        modals.confirm('Leave', props.unsavedDataMessage, ButtonStyle.NEGATIVE, {
          promiseCompatible: true,
          enableRedesign: true,
        })
      );
      if (!confirm) {
        return;
      }
    }
    analytics.trackEvent(SCHEMAS_ANALYTICS_ACTIONS.CLICKED_EDIT_SCHEMA_AS_JSON, {
      schema_type:
        props.schemaType === LegacyAssaySchemaType.ASSAY_RUN
          ? ModelTypeConstants.RUN_SCHEMA
          : ModelTypeConstants.RESULT_SCHEMA,
      schema_id: props.schemaId,
      session_id: props.sessionId,
    });
    BenchlingUtil.navigate(null, getJsonUrl(props.schemaType, props.schemaId));
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleFieldsChange = (fields: Array<ClientSchemaField>) => {
    if (props.schema == null) {
      getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecFields(fields));
    } else {
      const fieldsWithoutCid: Array<SchemaField> = fields.map((field) => {
        const {cid, ...fieldWithoutCid} = field;
        return fieldWithoutCid;
      }) as Array<SchemaField>; // TODO(30720) Fix SchemaField/ClientSchemaField type issues

      if (isEqual(fieldsWithoutCid, props.fields)) {
        return;
      }

      const draftSpec = getSpecForRequest(props);
      if (draftSpec == null) {
        return;
      }
      const newFields = massageFieldsForSubmitWithSpecErrorHandling(fields);
      if (newFields == null) {
        return;
      }

      // update the spec with the fields
      const spec = {...draftSpec, fields: newFields};

      updateAssaySchema({spec, draft: draftSpec});
    }
  };

  function getDisableSubmitWithTooltip(): string | null {
    const specIsValid = Object.values(componentsAreValid).every((componentIsValid) => componentIsValid);
    if (!props.name) {
      return 'Schema needs a name';
    }
    if (props.fields?.some((f) => !f.name)) {
      return 'Schema fields must all have names';
    }
    if (!specIsValid) {
      return 'Spec is invalid'; // when does this happen?
    }

    return null;
  }

  const disableSubmitWithTooltip = getDisableSubmitWithTooltip();

  const dataTestAttrs = {
    switchJson: {...props.baseTestAttrs, element: 'switchJson'},
  };

  return (
    <div className="u-flex-grow u-min-width-zero">
      <div className="settingsPageContainer-section">
        <SchemaMetadataSection
          isEditableWarehouseTableName={
            BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && shouldAllowSchemaUpdates()
          }
          name={props.name}
          ownerId={props.ownerId}
          ownerChoices={props.ownerChoices}
          sqlIdentifier={props.sqlIdentifier}
          onNameChange={(name: string) => {
            getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecName(name));
            if (props.autosave && props.schema != null) {
              updateAssaySchema({name});
            }
          }}
          onOwnerChange={(owner) => {
            assert(props.schema == null, 'Owner should only be editable when creating a new schema');
            getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecOwner(owner.id));
          }}
          onSqlIdentifierChange={(sqlIdentifier: string) => {
            getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecSqlIdentifier(sqlIdentifier));
            if (props.autosave && props.schema != null) {
              updateAssaySchema({sqlIdentifier});
            }
          }}
          schema={props.schema}
        />
        {props.schemaType === LegacyAssaySchemaType.ASSAY_RUN && (
          <RunSchemaMetadataSection
            connectionSchemaId={props.connectionSchemaId}
            allowInsertRunFromInbox={props.allowInsertRunFromInbox}
            allowCreateRunFromNotebook={props.allowCreateRunFromNotebook}
            shouldRecordEquipment={props.shouldRecordEquipment}
            isCreating={props.schema == null}
            onUpdate={() => {
              if (props.autosave && props.schema != null) {
                updateAssaySchema();
              }
            }}
          />
        )}
      </div>
      <div>
        <ErrorList errors={specErrors.length ? specErrors : null} errorMessage={props.errorMessage} />
        <br />
      </div>
      <div className={css(assaySchemaEditorStyles.fieldSection)}>
        <AssaySchemaErrorList errors={fieldsErrors} component="fields configuration" />
        {queryResult.type === QueryResultType.SUCCESS ? (
          <FieldTable
            schemaId={props.schema ? props.schema.id : null}
            schema={props.schema}
            schemaHasData={props.schemaHasData}
            schemaFieldDependentsSummariesById={props.schemaFieldDependentsSummariesById}
            isBeingCreated={props.schema == null}
            isDisabled={!shouldAllowSchemaUpdates()}
            itemClass={
              props.schemaType === LegacyAssaySchemaType.ASSAY_RESULT
                ? ResultSchemaFieldTableRow
                : RunSchemaFieldTableRow
            }
            fields={props.fields || []}
            onFieldsChange={handleFieldsChange}
            schemaType={SCHEMA_TYPE_NAME[props.schemaType]}
            batchSchemas={[]}
            storableSchemas={linkableSchemas.storableSchemas}
            assaySchemas={linkableSchemas.assaySchemas}
            savingState={SavingState.COMPLETED}
            ownerUrlId={getOwnerById(props.ownerId)?.url_identifier || ''}
            ownerId={props.ownerId}
            shouldShowIsRequired={true}
            shouldShowIsIntegration={false}
            shouldShowIsParentLink={false}
            dataTestAttrs={{component: 'AssaySchemaMetadataSettings', element: 'fieldTable'}}
          />
        ) : (
          <FieldTableLoading schemaType={SCHEMA_TYPE_NAME[props.schemaType]} />
        )}
      </div>
      {props.schemaType === LegacyAssaySchemaType.ASSAY_RUN && props.schema != null && (
        <>
          <div>
            <AutomationInputFileConfigListEditor
              baseSchema={props.schema as AssayRunSchema}
              errors={inputFileConfigErrors}
              automationInputFileConfigs={props.inputFileConfigs}
              onChange={(newValue: Array<AutomationInputFileConfigSpec>) => {
                getStore().dispatch(SchemaConfigReduxActions.setSchemaInputFileConfig(newValue));
              }}
              onValidationChange={(isValid) => {
                setComponentsAreValid({...componentsAreValid, automationInputFileConfigs: isValid});
              }}
            />
          </div>
          <div>
            <AutomationOutputFileConfigListEditor
              baseSchema={props.schema as AssayRunSchema}
              connectionSchemaId={props.connectionSchemaId}
              errors={outputFileConfigErrors}
              automationOutputFileConfigs={props.outputFileConfigs}
              onChange={(newValue: Array<ClientAutomationOutputFileConfigSpec>) => {
                getStore().dispatch(SchemaConfigReduxActions.setSchemaOutputFileConfig(newValue));
              }}
              onValidationChange={(isValid) => {
                setComponentsAreValid({...componentsAreValid, automationOutputFileConfigs: isValid});
              }}
              ownerId={props.ownerId}
            />
          </div>
        </>
      )}
      {(props.schemaId == null || !props.autosave) && shouldAllowSchemaUpdates() && (
        <TooltipWrapper tooltipTitle={disableSubmitWithTooltip}>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={disableSubmitWithTooltip != null}
            onClick={() => {
              onButtonClick();
            }}
            size={ButtonSize.MEDIUM}
            text={buttonText}
            dataTestAttrs={{...props.baseTestAttrs, element: dataTestAttributeElement}}
          />
        </TooltipWrapper>
      )}
      {shouldAllowSchemaUpdates() && (
        <Button
          className="u-small-spacing-right"
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          id="editAsJSONButton"
          text="Edit as JSON"
          onClick={() => {
            onEditJSONClick();
          }}
          dataTestAttrs={dataTestAttrs.switchJson}
        />
      )}
    </div>
  );
}

export const assaySchemaEditorStyles = StyleSheet.create({
  fieldSection: {
    marginBottom: '32px',
  },
});
